import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { buildActiveFilterChips } from '../constants/filterChips';
import PillModeToggle from './PillModeToggle';
import BulkExcludeControl from './BulkExcludeControl';

/**
 * Unified-filter model — one chip rail for EVERY active filter, replacing the
 * separate spotlights bar + keyword pill rail. Each active spotlight preset,
 * numeric range, controlled set, location, and keyword dimension renders as one
 * removable chip (built by the pure `buildActiveFilterChips`); a keyword chip
 * carries its All/Any mode badge. "Clear all" is a true global reset.
 *
 * Shared by the SearchResults page and the Projects results view (same store
 * shape). Renders nothing unless `visible` and at least one filter is active.
 */
const UnifiedChipRail = observer(({ store, variant, visible, showBulkExclude = true }) => {
  if (!visible) return null;

  const chips = buildActiveFilterChips(store.activeSpotlights, store.columnFilters, {
    categoryExcludedSpotlightIds: store.categoryExcludedSpotlightIds
  });
  if (!chips.length) return null;

  const removeChip = (chip) => {
    if (chip.kind === 'spotlight') store.toggleSpotlight(chip.key);
    else store.clearColumnFilter(chip.key);
  };

  return (
    <div className={`profiler-search-filter-rail profiler-search-filter-rail--${variant} unified-chip-rail`}>
      <div className="filters-chips-list-main-wrap">
        <div className="filters-chips-list-text">Filters ({chips.length})</div>
        <div className="filters-chips-list">
          {chips.map((chip) => (
            <div className="filters-chips-item unified-chip noselect" key={`${chip.kind}:${chip.key}`}>
              <div className="display-text">
                {chip.valueText ? (
                  <>
                    <span className="unified-chip-key">{chip.label}:</span>
                    <span className="unified-chip-value">{chip.valueText}</span>
                  </>
                ) : (
                  chip.label
                )}
              </div>
              {chip.kind === 'keyword' && chip.values.length >= 2 && (
                <PillModeToggle
                  mode={chip.mode}
                  onSetMode={(m) => store.setPillFilterMode(chip.key, m)}
                />
              )}
              <IconButton
                icon={ICON_BUTTON_ICONS.X_LG}
                type={ICON_BUTTON_TYPES.BLUE}
                withFill={false}
                withBorder={false}
                onClick={() => removeChip(chip)}
                tooltipText={`Remove ${chip.label} filter`}
              />
            </div>
          ))}
          <button
            type="button"
            className="filters-chips-clear-all"
            onClick={() => store.clearAllFilters()}
          >
            Clear all
          </button>
        </div>
        {/* TD-2461 × TD-2560 — bulk exclusion lives with the filters it acts on.
            Rendering it here (in the shared rail) gives both Search and the
            saved-project results view the control, right next to the active
            filters. Desktop only; the store self-gates the two directions.
            TD-2601 — callers pass showBulkExclude={false} where the action would
            be a no-op or destructive (e.g. the project Excluded tab, where the
            rows are already excluded); default true keeps Search unchanged. */}
        {variant === 'desktop' && showBulkExclude && typeof store.excludeMatching === 'function' && (
          <BulkExcludeControl store={store} />
        )}
      </div>
    </div>
  );
});

UnifiedChipRail.propTypes = {
  store: PropTypes.object.isRequired,
  variant: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  showBulkExclude: PropTypes.bool
};

export default UnifiedChipRail;
